// src/pages/FundPlanner/components/Portfolios/Main/index.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import TabMenu from "components/TabMenu";
import { useProfile } from "context/ProfileContext";

import usePortfolioReportPositions from "../usePortfolioReportPositions";
import {
  trimToEmpty,
} from "../portfolioReportPositions";
import {
  buildAllocation,
  buildCreditRatingSummary,
  buildMaturityBuckets,
  buildOverviewMetrics,
  buildPeriodOverviewData,
  buildTopHoldings,
  getReportOverviewMetrics,
} from "./portfolioMainReportData";
import { buildFundOverviewData } from "./fundOverviewData";

import OverviewReport from "./OverviewReport";
import FundOverviewReport from "./FundOverviewReport";
import MaturityReport from "./MaturityReport";
import HoldingsReport from "./HoldingsReport";
import AllocationReport from "./AllocationReport";
import exportPortfolioMainPdf from "pages/FundPlanner/reports/exportPortfolioMainPdf";
import { getPortfolioPeriodOpeningDate } from "../portfolioPeriodDates";

/* ────────────────────────────────
   Constants
──────────────────────────────── */
const BASE_TABS = [
  { key: "overview", label: "Portfolio Overview" },
  { key: "allocation", label: "Allocation" },
  { key: "maturity", label: "Maturities" },
  { key: "holdings", label: "Top Holdings" },
];

function formatDate(value) {
  if (!value) return "";
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

/* ────────────────────────────────
   Component
──────────────────────────────── */
export default function Main({
  reportScope,
  reportData = null,
  portfolioName = "",
  onExportStateChange,
}) {
  const { selectedOrg } = useProfile();
  const [activeTab, setActiveTab] = useState("overview");
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const {
    isLoading,
    loadError,
    rows,
    funds,
    positions,
    asOfDate,
    reportMode,
    startDate,
    endDate,
    periodAnalysis,
  } = usePortfolioReportPositions(reportScope, {
    enabled: !reportData,
    data: reportData,
  });

  const overviewMetrics = useMemo(() => buildOverviewMetrics(rows), [rows]);
  const periodOverview = useMemo(
    () => buildPeriodOverviewData(periodAnalysis),
    [periodAnalysis]
  );
  const reportOverviewMetrics = useMemo(
    () => getReportOverviewMetrics(overviewMetrics, reportMode, periodOverview),
    [overviewMetrics, periodOverview, reportMode]
  );
  const fundOverview = useMemo(
    () => buildFundOverviewData({ funds, positions, rows, reportMode, periodAnalysis }),
    [funds, periodAnalysis, positions, reportMode, rows]
  );
  const tabs = useMemo(
    () => [...BASE_TABS, { key: "funds", label: "Fund Overview" }],
    []
  );
  const hasReportData = reportMode === "period"
    ? Boolean(periodOverview)
    : rows.length > 0;
  const maturityData = useMemo(() => buildMaturityBuckets(rows), [rows]);

  const allocationByType = useMemo(
    () => buildAllocation(rows, (row) => trimToEmpty(row.investment_type) || "Unknown"),
    [rows]
  );

  const creditRatingSummary = useMemo(
    () => buildCreditRatingSummary(rows),
    [rows]
  );

  const topHoldings = useMemo(() => buildTopHoldings(rows, 10), [rows]);
  const reportAsOfDate = reportMode === "period" ? endDate : asOfDate;
  const reportAsOfSubtitle = reportAsOfDate
    ? `As of ${formatDate(reportAsOfDate)}`
    : "";
  const portfolioOverviewSubtitle = reportMode === "period"
    ? `${formatDate(getPortfolioPeriodOpeningDate(startDate))} EOD to ${formatDate(endDate)} EOD`
    : reportAsOfSubtitle;

  const onPdfExport = useCallback(async () => {
    try {
      setIsPdfGenerating(true);

      await exportPortfolioMainPdf({
        orgId: selectedOrg?.id,
        rows,
        asOfDate,
        reportMode,
        startDate,
        endDate,
        periodAnalysis,
        funds,
        positions,
        orgName: selectedOrg?.name || "",
        portfolioName,
      });
    } catch (err) {
      console.warn("FundPlanner Main PDF export failed:", err);
      alert(`PDF export failed: ${err?.message || String(err)}`);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [
    asOfDate,
    endDate,
    funds,
    periodAnalysis,
    portfolioName,
    positions,
    reportMode,
    rows,
    selectedOrg?.id,
    selectedOrg?.name,
    startDate,
  ]);

  useEffect(() => {
    onExportStateChange?.({
      visible: !isLoading && !loadError && hasReportData,
      disabled: isPdfGenerating,
      label: isPdfGenerating ? "Generating PDF..." : "Export PDF",
      onClick: onPdfExport,
    });
  }, [
    isLoading,
    loadError,
    isPdfGenerating,
    onExportStateChange,
    onPdfExport,
    hasReportData,
  ]);

  useEffect(() => {
    return () => {
      onExportStateChange?.({
        visible: false,
        disabled: true,
        label: "Export PDF",
        onClick: null,
      });
    };
  }, [onExportStateChange]);

  return (
    <div className="space-y-4">
      <TabMenu tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {isLoading && (
        <SectionCard title="Summary">
          <div className="text-sm text-muted">Loading summary data…</div>
        </SectionCard>
      )}

      {!isLoading && loadError && (
        <SectionCard title="Summary">
          <div className="text-sm text-red-600 dark:text-red-400">{loadError}</div>
        </SectionCard>
      )}

      {!isLoading && !loadError && !hasReportData && (
        <SectionCard title="Summary">
          <div className="text-sm text-muted">
            No report data was found for the selected scope and date range.
          </div>
        </SectionCard>
      )}

      {!isLoading && !loadError && hasReportData && activeTab === "overview" && (
        <SectionCard title="Key Portfolio Metrics" subtitle={portfolioOverviewSubtitle}>
          <OverviewReport
            metrics={reportOverviewMetrics}
            reportMode={reportMode}
            periodOverview={periodOverview}
          />
        </SectionCard>
      )}

      {!isLoading && !loadError && fundOverview && activeTab === "funds" && (
        <SectionCard
          title="Fund Overview"
          subtitle={reportMode === "period"
            ? `${formatDate(startDate)} to ${formatDate(endDate)}`
            : reportAsOfSubtitle}
        >
          <FundOverviewReport data={fundOverview} />
        </SectionCard>
      )}

      {!isLoading && !loadError && rows.length > 0 && activeTab === "maturity" && (
        <SectionCard title="Maturity Distribution" subtitle={reportAsOfSubtitle}>
          <MaturityReport
            buckets={maturityData.buckets}
            totalMarketValue={maturityData.totalMarketValue}
          />
        </SectionCard>
      )}

      {!isLoading && !loadError && rows.length > 0 && activeTab === "holdings" && (
        <SectionCard title="Top Holdings" subtitle={reportAsOfSubtitle}>
          <HoldingsReport rows={topHoldings} />
        </SectionCard>
      )}

      {!isLoading && !loadError && rows.length > 0 && activeTab === "allocation" && (
        <SectionCard title="Asset Allocation" subtitle={reportAsOfSubtitle}>
          <AllocationReport
            allocationByType={allocationByType.items}
            creditRatingSummary={creditRatingSummary}
          />
        </SectionCard>
      )}
    </div>
  );
}
